import React, { useEffect, useMemo, useState } from 'react';

import { workoutService } from '../services/workoutService';

import './ProgressTracker.css';

const formatDate = (value) => {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatDuration = (minutes) => {
  if (!minutes) {
    return '—';
  }
  if (minutes < 60) {
    return `${minutes} мин`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} ч` : `${hours} ч ${rest} мин`;
};

const ProgressTracker = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        const summaryData = await workoutService.getProgressSummary();
        setSummary(summaryData);
      } catch (err) {
        console.error('Error loading progress summary:', err);
        setError('Не удалось загрузить прогресс. Попробуйте обновить страницу.');
      } finally {
        setLoading(false);
      }
    };

    loadSummary();
  }, []);

  const completionPercent = useMemo(
    () => (summary ? Math.min(100, Math.max(0, summary.completionRate)) : 0),
    [summary]
  );

  return (
    <div className="progress-tracker">
      <div className="progress-header">
        <h2>Мой прогресс</h2>
        <p>Следи за своим движением вперёд и отмечай каждую победу</p>
      </div>

      {loading ? (
        <div className="loading-text">Собираем ваши данные...</div>
      ) : error ? (
        <div className="error-text">{error}</div>
      ) : !summary ? (
        <div className="empty-state-card">
          <h3>Пока нет данных</h3>
          <p>Начните первую тренировку, чтобы увидеть прогресс.</p>
        </div>
      ) : (
        <div className="tab-content">
              <div className="stats-row">
                <div className="stat-card primary">
                  <div className="stat-label">Завершено тренировок</div>
                  <div className="stat-value">
                    {summary.workoutsCompleted}
                    <span>из {summary.totalWorkouts}</span>
                  </div>
                  <div className="stat-change">
                    Программа выполнена на {completionPercent}%
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-label">Осталось пройти</div>
                  <div className="stat-value">
                    {summary.workoutsRemaining}
                  </div>
                  <div className="stat-change">
                    Активных тренировок: {summary.workoutsInProgress}
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-label">Текущая серия</div>
                  <div className="stat-value">
                    {summary.currentStreakDays}
                    <span>дн</span>
                  </div>
                  <div className="stat-change">
                    Лучшая серия: {summary.bestStreakDays} дн
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-label">Активных дней</div>
                  <div className="stat-value">
                    {summary.totalTrainingDays}
                  </div>
                  <div className="stat-change">
                    За 30 дней: {summary.workoutsLast30Days} трен.
                  </div>
                </div>
              </div>

              <div className="summary-grid">
                <div className="summary-card">
                  <span className="summary-label">Начал тренироваться</span>
                  <span className="summary-value">{formatDate(summary.firstWorkoutDate)}</span>
                  {summary.firstWorkoutName && (
                    <span className="summary-caption">{summary.firstWorkoutName}</span>
                  )}
                </div>
                <div className="summary-card">
                  <span className="summary-label">Последняя тренировка</span>
                  <span className="summary-value">{formatDate(summary.lastWorkoutDate)}</span>
                  <span className="summary-caption">
                    {summary.workoutsInProgress > 0
                      ? 'Есть активная тренировка'
                      : 'Все тренировки завершены'}
                  </span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Средняя активность</span>
                  <span className="summary-value">
                    {summary.averageWorkoutsPerWeek}
                    <span> трен./нед</span>
                  </span>
                  <span className="summary-caption">За последние 30 дней</span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Средняя длительность</span>
                  <span className="summary-value">
                    {formatDuration(summary.averageWorkoutDurationMinutes)}
                  </span>
                  <span className="summary-caption">По завершённым тренировкам</span>
                </div>
              </div>

              <div className="workout-progress-card">
                <h3>Прогресс по программе</h3>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${completionPercent}%` }}
                  />
                </div>
                <div className="progress-details">
                  <span>{summary.workoutsCompleted} завершено</span>
                  <span>{summary.workoutsRemaining} осталось</span>
                </div>
              </div>

              <div className="insights-grid">
                <div className="insight-card">
                  <div className="insight-label">Выполнено упражнений</div>
                  <div className="insight-value">{summary.totalExercisesCompleted}</div>
                  <div className="insight-caption">
                    В среднем {summary.averageExercisesPerWorkout} за тренировку
                  </div>
                </div>
                <div className="insight-card">
                  <div className="insight-label">Активность за 30 дней</div>
                  <div className="insight-value">{summary.workoutsLast30Days}</div>
                  <div className="insight-caption">
                    {summary.averageWorkoutsPerWeek} тренировки/неделю
                  </div>
                </div>
                <div className="insight-card">
                  <div className="insight-label">Тренировочных дней</div>
                  <div className="insight-value">{summary.totalTrainingDays}</div>
                  <div className="insight-caption">Последняя: {formatDate(summary.lastWorkoutDate)}</div>
                </div>
              </div>

              <div className="recent-workouts-card">
                <h3>Недавние тренировки</h3>
                {summary.recentWorkouts && summary.recentWorkouts.length > 0 ? (
                  <div className="recent-workouts-list">
                    {summary.recentWorkouts.map((item) => (
                      <div key={`${item.workoutId}-${item.startedAt ?? item.completedAt}`} className="recent-workout-row">
                        <div className="workout-status">
                          {item.completed ? '✅' : '⏳'}
                        </div>
                        <div className="workout-info">
                          <span className="workout-name">
                            {item.workoutNumber ? `#${item.workoutNumber} ` : ''}
                            {item.workoutName ?? 'Тренировка'}
                          </span>
                          <span className="workout-meta">
                            Начало: {formatDate(item.startedAt)} • Завершение: {formatDate(item.completedAt)}
                          </span>
                        </div>
                        <div className="workout-duration">
                          {formatDuration(item.durationMinutes)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">Тренировки ещё не завершены.</div>
                )}
              </div>
            </div>
      )}
    </div>
  );
};

export default ProgressTracker;
